"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import { motion } from "framer-motion";
import Logo from "@/components/Logo";
import { login, signup } from "@/lib/api";

const ROLES: { key: "monteur" | "opdrachtgever"; label: string }[] = [
  { key: "monteur", label: "Monteur" },
  { key: "opdrachtgever", label: "Opdrachtgever" },
];

function SignupForm() {
  const router = useRouter();
  const params = useSearchParams();
  const initialRole =
    (params.get("role") as "opdrachtgever" | "monteur") ?? "monteur";

  const [role, setRole] = useState<"opdrachtgever" | "monteur">(initialRole);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await signup({
        email,
        password,
        first_name: first,
        last_name: last,
        role,
      });
      await login(email, password);
      router.replace(
        role === "monteur"
          ? "/monteur/onboarding/welkom"
          : "/opdrachtgever",
      );
    } catch {
      setErr("Aanmelden mislukt. Controleer je gegevens.");
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    "w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2.5 text-[14px] text-[var(--ink)] outline-none transition-colors focus:border-[var(--ink)]";

  return (
    <motion.form
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      onSubmit={onSubmit}
      className="w-full max-w-sm space-y-5 rounded-3xl border border-[var(--line)] bg-[var(--surface-raised)] p-8 shadow-lg"
    >
      <div className="space-y-1">
        <h1 className="text-[22px] font-semibold tracking-[-0.02em] text-[var(--ink)]">
          Account aanmaken
        </h1>
        <p className="text-[13px] text-[var(--ink-3)]">
          Kies waarvoor je je aanmeldt.
        </p>
      </div>

      {/* Rol-switcher met glijdende pill */}
      <div className="relative flex rounded-full border border-[var(--line)] bg-[var(--surface-sunk)] p-1">
        {ROLES.map((r) => {
          const active = role === r.key;
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => setRole(r.key)}
              className="relative flex-1 px-3 py-2 text-[13px] font-medium"
            >
              {active && (
                <motion.span
                  layoutId="signup-role-pill"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  className="absolute inset-0 rounded-full bg-[var(--surface-raised)] shadow-sm"
                />
              )}
              <span className={`relative ${active ? "text-[var(--ink)]" : "text-[var(--ink-3)]"}`}>
                {r.label}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="mb-1.5 block text-[12px] font-medium text-[var(--ink-2)]">Voornaam</span>
          <input required value={first} onChange={(e) => setFirst(e.target.value)} className={inputCls} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-[12px] font-medium text-[var(--ink-2)]">Achternaam</span>
          <input required value={last} onChange={(e) => setLast(e.target.value)} className={inputCls} />
        </label>
      </div>

      <label className="block">
        <span className="mb-1.5 block text-[12px] font-medium text-[var(--ink-2)]">E-mail</span>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
      </label>

      <label className="block">
        <span className="mb-1.5 block text-[12px] font-medium text-[var(--ink-2)]">Wachtwoord <span className="font-normal text-[var(--ink-3)]">(min. 8)</span></span>
        <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
      </label>

      {err && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-lg bg-[#FEECEC] px-3 py-2 text-[13px] text-[#C0392B]"
        >
          {err}
        </motion.p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="w-full rounded-full bg-[var(--brand)] px-4 py-3 text-[14px] font-medium text-[var(--brand-ink)] shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[var(--brand-strong)] hover:text-white hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
      >
        {busy ? "Bezig…" : "Account aanmaken"}
      </button>
    </motion.form>
  );
}

export default function SignupPage() {
  return (
    <main className="flex flex-1 flex-col items-center justify-center px-6 py-16">
      <Link href="/" aria-label="Naar home" className="mb-8">
        <Logo size={22} />
      </Link>
      <Suspense fallback={null}>
        <SignupForm />
      </Suspense>
      <p className="mt-6 text-[13px] text-[var(--ink-3)]">
        Al een account?{" "}
        <Link href="/login" className="font-medium text-[var(--ink)] underline underline-offset-4 hover:text-[var(--brand-strong)]">
          Inloggen
        </Link>
      </p>
    </main>
  );
}
